// BEGIN FILE: src/components/widgets/MarriageCompatibilityWidget.tsx
/**
 * Marriage Compatibility Widget
 * 
 * Comprehensive Vedic marriage compatibility analysis between two charts including:
 * - Dual chart visualization
 * - Emotional dynamics (Moon sign distance)
 * - Gana, Nakshatra lord, Yoni compatibility
 * - Lagna compatibility
 * - 7th house analysis
 * - Venus & Jupiter capacity analysis
 */

import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Card,
  Typography,
  Select,
  Option,
  Sheet,
  Chip,
  LinearProgress,
  Alert,
  Button,
  Grid,
  Divider,
  Stack,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Autocomplete,
} from '@mui/joy';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import FavoriteIcon from '@mui/icons-material/Favorite';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { supabase } from '../../utils/supabaseClient';
import {
  calculateMarriageCompatibility,
  type PartnerChart,
  type MarriageCompatibilityResult,
  type ManglikDoshaAnalysis,
} from '../../utils/marriageCompatibility';
import { getUserPermanentYogas } from '../../utils/yogaStorage';
import {
  getGrahaLongitudesAndMotionSidereal,
  getRahuKetuSidereal,
  getAscendantSignAndNakshatra,
  assignPlanetsToHouses,
  classifyFunctionalForLagna,
  computeImplementedVargas,
  vargaToPlacements,
  type PlanetName,
  RASHI_NAMES,
  NAKSHATRA_NAMES,
} from '../../utils/astroEngine';
import { DateTime } from 'luxon';
import AdvancedNorthIndianChart from '../AdvancedNorthIndianChart';

// ===== TYPES =====

interface BirthProfile {
  id: string;
  owner_id: string;
  full_name: string;
  gender: string | null;
  relationship: string | null;
  dob_day: number;
  dob_month: number;
  dob_year: number;
  tob_hour: number;
  tob_minute: number;
  tob_second: number | null;
  place_label: string;
  place_lat: number;
  place_lng: number;
  place_timezone: string;
  chart_data: any | null;
  mangal_dosha?: {
    present: boolean;
    severity?: string;
    houses?: number[];
    cancelled?: boolean;
  } | null;
}

interface Props {
  userId: string;
  onNavigateToBirthDetails?: () => void;
}

// ===== HELPER FUNCTIONS =====

function getSignLord(signNum: number): PlanetName {
  const lords: Record<number, PlanetName> = {
    1: 'Mars', 2: 'Venus', 3: 'Mercury', 4: 'Moon',
    5: 'Sun', 6: 'Mercury', 7: 'Venus', 8: 'Mars',
    9: 'Jupiter', 10: 'Saturn', 11: 'Saturn', 12: 'Jupiter',
  };
  return lords[signNum] || 'Sun';
}

function getNakshatraLord(nakshatra: string): PlanetName {
  const nakshatraToLord: Record<string, PlanetName> = {
    'Ashwini': 'Ketu', 'Bharani': 'Venus', 'Krittika': 'Sun', 'Rohini': 'Moon',
    'Mrigashira': 'Mars', 'Ardra': 'Rahu', 'Punarvasu': 'Jupiter', 'Pushya': 'Saturn',
    'Ashlesha': 'Mercury', 'Magha': 'Ketu', 'Purva Phalguni': 'Venus', 'Uttara Phalguni': 'Sun',
    'Hasta': 'Moon', 'Chitra': 'Mars', 'Swati': 'Rahu', 'Vishakha': 'Jupiter',
    'Anuradha': 'Saturn', 'Jyeshtha': 'Mercury', 'Mula': 'Ketu', 'Purva Ashadha': 'Venus',
    'Uttara Ashadha': 'Sun', 'Shravana': 'Moon', 'Dhanishta': 'Mars', 'Shatabhisha': 'Rahu',
    'Purva Bhadrapada': 'Jupiter', 'Uttara Bhadrapada': 'Saturn', 'Revati': 'Mercury',
  };
  return nakshatraToLord[nakshatra] || 'Sun';
}

// ===== MAIN COMPONENT =====

export default function MarriageCompatibilityWidget({ userId, onNavigateToBirthDetails }: Props) {
  const [profiles, setProfiles] = useState<BirthProfile[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedProfile1, setSelectedProfile1] = useState<string>('');
  const [selectedProfile2, setSelectedProfile2] = useState<string>('');
  const [partner1Data, setPartner1Data] = useState<PartnerChart | null>(null);
  const [partner2Data, setPartner2Data] = useState<PartnerChart | null>(null);
  const [compatibilityResult, setCompatibilityResult] = useState<MarriageCompatibilityResult | null>(null);
  const [calculating, setCalculating] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);

  // Check if user is admin
  useEffect(() => {
    async function checkAdminStatus() {
      try {
        const { data: profile } = await supabase
          .from('profiles')
          .select('role')
          .eq('id', userId)
          .single();
        
        setIsAdmin(profile?.role === 'admin');
      } catch (err) {
        console.error('[MarriageCompatibilityWidget] Error checking admin status:', err);
      }
    }
    checkAdminStatus();
  }, [userId]);

  // Load user's birth profiles
  useEffect(() => {
    loadProfiles();
  }, [userId, isAdmin]); // Re-run when admin status changes

  async function loadProfiles() {
    setLoading(true);
    try {
      // Admin sees all profiles, regular users see only their own
      let query = supabase
        .from('birth_profiles')
        .select('*');
      
      if (!isAdmin) {
        query = query.eq('owner_id', userId);
      }
      
      const { data, error } = await query.order('created_at', { ascending: false });

      if (error) throw error;
      
      // Filter profiles for non-admin users: only Self, Spouse, Other
      const allProfiles = (data || []) as BirthProfile[];
      const filteredProfiles = isAdmin 
        ? allProfiles 
        : allProfiles.filter(p => 
            ['Self', 'Spouse', 'Other'].includes(p.relationship || 'Self')
          );
      
      setProfiles(filteredProfiles);
    } catch (err) {
      console.error('[MarriageCompatibilityWidget] Error loading profiles:', err);
    } finally {
      setLoading(false);
    }
  }

  // Helper function to calculate which planets aspect a given house (Parashari aspects)
  function getPlanetsAspectingHouse(targetHouse: number, housePlacements: any): PlanetName[] {
    const aspectingPlanets: PlanetName[] = [];
    const planets: PlanetName[] = ['Sun', 'Moon', 'Mars', 'Mercury', 'Jupiter', 'Venus', 'Saturn', 'Rahu', 'Ketu'];
    
    // Parashari aspect offsets (same as used in App.tsx)
    const PARASHARI_OFFSETS: Record<string, number[]> = {
      Sun: [7], 
      Moon: [7], 
      Mercury: [7], 
      Venus: [7],
      Mars: [4, 7, 8],      // 4th, 7th, 8th aspects
      Jupiter: [5, 7, 9],   // 5th, 7th, 9th aspects
      Saturn: [3, 7, 10],   // 3rd, 7th, 10th aspects
      Rahu: [5, 7, 9],      // Same as Jupiter
      Ketu: [5, 7, 9]       // Same as Jupiter
    };
    
    for (const planet of planets) {
      const placement = housePlacements[planet];
      if (!placement) continue;
      
      const planetHouse = placement.house;
      const offsets = PARASHARI_OFFSETS[planet] || [7];
      
      // Check if any aspect targets the target house
      for (const offset of offsets) {
        const aspectedHouse = ((planetHouse - 1 + (offset - 1)) % 12) + 1;
        if (aspectedHouse === targetHouse) {
          aspectingPlanets.push(planet);
          break; // Avoid duplicate entries
        }
      }
    }
    
    return aspectingPlanets;
  }

  // Extract Mangal Dosha from existing profile yogas/chart_data
  async function extractMangalDoshaFromProfile(profile: BirthProfile, partnerName: string, marsHouse: number): Promise<ManglikDoshaAnalysis | undefined> {
    try {
      // First, try to get from stored yogas in database
      const storedYogas = await getUserPermanentYogas(userId, profile.id);
      
      const mangalDoshaYoga = storedYogas.find(y => 
        y.yoga_key === 'mangal_dosha_natal' || 
        y.title?.toLowerCase().includes('mangal dosha') ||
        y.title?.toLowerCase().includes('kuja dosha')
      );
      
      if (mangalDoshaYoga) {
        // Parse the description to extract cancellation info
        const desc = mangalDoshaYoga.description || '';
        const isCancelled = desc.toLowerCase().includes('cancellation') || desc.toLowerCase().includes('cancelled');
        const cancellationFactors: string[] = [];
        
        if (isCancelled) {
          // Extract cancellation reasons from description
          if (desc.includes('exalted')) cancellationFactors.push('Mars exalted in Capricorn - strength overcomes affliction');
          if (desc.includes('own sign')) cancellationFactors.push('Mars in own sign (Aries/Scorpio) - comfortable placement neutralizes dosha');
          if (desc.includes('Jupiter') && desc.includes('aspect')) cancellationFactors.push('Jupiter aspects Mars - wisdom neutralizes aggression');
          if (desc.includes('Venus') && desc.includes('aspect')) cancellationFactors.push('Venus aspects Mars - love softens conflict');
        }
        
        const houseEffects: Record<number, string> = {
          1: 'Dominance - tendency to control or overpower partner',
          2: 'Family disputes - conflicts with in-laws and family matters',
          4: 'Domestic unrest - home environment instability',
          7: 'Direct marital conflict - friction in partnership',
          8: 'Separation tendencies - challenges in sustaining relationship',
          12: 'Sexual frustration - intimacy and satisfaction issues',
        };
        
        // Determine severity from strength and cancellation
        let severity: 'high' | 'moderate' | 'low' | 'none' = 'none';
        if (mangalDoshaYoga.type === 'malefic' && !isCancelled) {
          if (mangalDoshaYoga.strength === 'strong' || mangalDoshaYoga.strength === 'very_strong') {
            severity = 'high';
          } else if (mangalDoshaYoga.strength === 'moderate') {
            severity = 'moderate';
          } else {
            severity = 'low';
          }
        }
        
        let recommendation = '';
        if (isCancelled) {
          recommendation = 'Manglik Dosha is cancelled. Perform basic Mars remedies (Hanuman worship) for peace of mind.';
        } else {
          if (severity === 'high') {
            recommendation = 'Strong Manglik Dosha. Essential: Mars remedies (visit Hanuman temple Tuesdays, wear red coral after consultation), marry another Manglik, or perform Kumbh Vivah ritual.';
          } else if (severity === 'moderate') {
            recommendation = 'Moderate Manglik Dosha. Recommended: Mars remedies (chant Hanuman Chalisa, donate red items on Tuesdays).';
          } else {
            recommendation = 'Mild Manglik Dosha. Optional: Simple Mars remedies for harmony.';
          }
        }
        
        return {
          person: partnerName,
          isManglik: true,
          marsHouse,
          effect: houseEffects[marsHouse] || 'Mars affliction affects marriage',
          severity,
          cancellationFactors,
          isCancelled,
          recommendation,
          fromExistingData: true,
        };
      }
      
      // Fallback: Check chart_data if yogas not found in database
      const chartData = profile.chart_data as any;
      if (chartData?.mangal_dosha || chartData?.mangalDosha) {
        const md = chartData.mangal_dosha || chartData.mangalDosha;
        if (md.present) {
          const houseEffects: Record<number, string> = {
            1: 'Dominance - tendency to control or overpower partner',
            2: 'Family disputes - conflicts with in-laws and family matters',
            4: 'Domestic unrest - home environment instability',
            7: 'Direct marital conflict - friction in partnership',
            8: 'Separation tendencies - challenges in sustaining relationship',
            12: 'Sexual frustration - intimacy and satisfaction issues',
          };

          const cancellationFactors: string[] = [];
          let isCancelled = md.cancelled || false;
          
          if (md.cancellationReasons) {
            cancellationFactors.push(...md.cancellationReasons);
          }
          
          let severity: 'high' | 'moderate' | 'low' | 'none' = 'none';
          if (md.severity) {
            const sevMap: Record<string, 'high' | 'moderate' | 'low'> = {
              'high': 'high',
              'very severe': 'high',
              'severe': 'high',
              'moderate': 'moderate',
              'medium': 'moderate',
              'mild': 'low',
              'low': 'low',
            };
            severity = sevMap[md.severity.toLowerCase()] || 'moderate';
          } else if (!isCancelled) {
            if ([7, 8].includes(marsHouse)) severity = 'high';
            else if ([1, 4].includes(marsHouse)) severity = 'moderate';
            else severity = 'low';
          }
          
          let recommendation = '';
          if (isCancelled) {
            recommendation = 'Manglik Dosha is cancelled. Perform basic Mars remedies (Hanuman worship) for peace of mind.';
          } else {
            if (severity === 'high') {
              recommendation = 'Strong Manglik Dosha. Essential: Mars remedies (visit Hanuman temple Tuesdays, wear red coral after consultation), marry another Manglik, or perform Kumbh Vivah ritual.';
            } else if (severity === 'moderate') {
              recommendation = 'Moderate Manglik Dosha. Recommended: Mars remedies (chant Hanuman Chalisa, donate red items on Tuesdays).';
            } else {
              recommendation = 'Mild Manglik Dosha. Optional: Simple Mars remedies for harmony.';
            }
          }
          
          return {
            person: partnerName,
            isManglik: true,
            marsHouse: md.houses?.[0] || marsHouse,
            effect: houseEffects[md.houses?.[0] || marsHouse] || 'Mars affliction affects marriage',
            severity,
            cancellationFactors,
            isCancelled,
            recommendation,
            fromExistingData: true,
          };
        }
      }
    } catch (err) {
      console.error('[MarriageCompatibilityWidget] Error fetching Mangal Dosha from database:', err);
    }
    
    return undefined;
  }

  // Calculate chart data for selected profile
  async function calculateChartForProfile(profile: BirthProfile): Promise<PartnerChart | null> {
    try {
      const { dob_day, dob_month, dob_year, tob_hour, tob_minute, tob_second, place_lat, place_lng, place_timezone } = profile;
      
      const dateStr = `${dob_year}-${String(dob_month).padStart(2, '0')}-${String(dob_day).padStart(2, '0')}`;
      const timeStr = `${String(tob_hour).padStart(2, '0')}:${String(tob_minute).padStart(2, '0')}:${String(tob_second || 0).padStart(2, '0')}`;
      const dtLocal = DateTime.fromISO(`${dateStr}T${timeStr}`, { zone: place_timezone });
      const dtUtc = dtLocal.toUTC();
      
      const isoDate = dtUtc.toISO({ suppressMilliseconds: true }) || '';
      const observerInput = { isoDate, latitude: place_lat, longitude: place_lng };

      // ===== READ FROM STORED CHART_DATA (like we do for D9) =====
      let housePlacements: any;
      let lagna: number;
      let planetLongitudes: Partial<Record<PlanetName, number>>;
      let moonSign: number;
      let moonNakshatra: string;
      let moonNakshatraLord: PlanetName;
      
      if (profile.chart_data?.placements) {
        // Use stored D1 placements from chart_data
        housePlacements = profile.chart_data.placements;
        lagna = profile.chart_data.ascendantSign;
        
        // Reconstruct planetLongitudes from stored siderealLongitude (full 0-360° value)
        planetLongitudes = {};
        Object.entries(housePlacements).forEach(([planetName, data]: [string, any]) => {
          if (data.siderealLongitude !== undefined) {
            // Use stored full longitude (most accurate)
            planetLongitudes[planetName as PlanetName] = data.siderealLongitude;
          } else if (data.signDeg !== undefined) {
            // Fallback: reconstruct from sign+signDeg
            planetLongitudes[planetName as PlanetName] = ((data.sign - 1) * 30) + data.signDeg;
          }
        });
        
        // Moon data from stored placements
        const moonData = housePlacements.Moon;
        moonSign = moonData?.sign || 1;
        
        // Use stored nakshatra if available (most accurate)
        if (moonData?.nakshatra) {
          moonNakshatra = moonData.nakshatra;
        } else {
          // Fallback: calculate from longitude
          const moonLonDeg = planetLongitudes.Moon || 0;
          const moonNakshatraIndex = Math.floor((moonLonDeg / 360) * 27);
          moonNakshatra = NAKSHATRA_NAMES[moonNakshatraIndex];
        }
        moonNakshatraLord = getNakshatraLord(moonNakshatra);
        
      } else {
        // Fallback: calculate fresh D1 (like we do for D9 fallback)
        
        // Calculate planetary positions
        const grahas = getGrahaLongitudesAndMotionSidereal(observerInput);
        const nodes = getRahuKetuSidereal(observerInput);
        const ascData = getAscendantSignAndNakshatra(isoDate, place_lat, place_lng);
        
        planetLongitudes = {
          Sun: grahas.Sun.lon,
          Moon: grahas.Moon.lon,
          Mercury: grahas.Mercury.lon,
          Venus: grahas.Venus.lon,
          Mars: grahas.Mars.lon,
          Jupiter: grahas.Jupiter.lon,
          Saturn: grahas.Saturn.lon,
          Rahu: nodes.Rahu,
          Ketu: nodes.Ketu,
        };

        // Generate cusps (whole sign system)
        const cusps: number[] = [];
        for (let i = 0; i < 12; i++) {
          const signIndex = (ascData.ascendantSignIndex - 1 + i) % 12;
          cusps.push(signIndex * 30);
        }

        housePlacements = assignPlanetsToHouses(cusps, planetLongitudes);
        lagna = ascData.ascendantSignIndex;

        // Moon data
        const moonLonDeg = grahas.Moon.lon;
        moonSign = Math.floor(moonLonDeg / 30) + 1;
        const moonNakshatraIndex = Math.floor((moonLonDeg / 360) * 27);
        moonNakshatra = NAKSHATRA_NAMES[moonNakshatraIndex];
        moonNakshatraLord = getNakshatraLord(moonNakshatra);
      }

      // House scores (simplified - would need full calculation in production)
      const h1Score = 0; // Placeholder
      const h7Score = 0; // Placeholder

      // Planets in 7th house (house 7, not sign 7)
      const planetsInH7: PlanetName[] = [];
      Object.entries(housePlacements).forEach(([planetName, data]: [string, any]) => {
        if (data && data.house === 7) {  // Check if planet is in house 7
          planetsInH7.push(planetName as PlanetName);
        }
      });
      
      // Planets aspecting 7th house (especially Jupiter for marriage protection)
      const aspectingH7 = getPlanetsAspectingHouse(7, housePlacements);
      
      // 7th house sign (for reference)
      const seventhHouseSign = ((lagna - 1 + 6) % 12) + 1;

      // 7th lord
      const seventhSign = ((lagna - 1 + 6) % 12) + 1;
      const seventhLord = getSignLord(seventhSign);
      const seventhLordData = housePlacements[seventhLord];
      const seventhLordHouse = seventhLordData?.house || 1;
      const seventhLordLonDeg = planetLongitudes[seventhLord] || 0;
      const seventhLordSign = Math.floor(seventhLordLonDeg / 30) + 1;

      // 7th lord state - retrograde from stored data or calculate fresh
      const seventhLordState = {
        exalted: false, // Would need proper calculation
        debilitated: false,
        ownSign: false,
        combust: false,
        retrograde: seventhLordData?.retrograde || false, // From stored placements
      };

      // Extract planet signs and dignity states from stored chart_data or housePlacements
      const venusSign = housePlacements.Venus?.sign || Math.floor((planetLongitudes.Venus || 0) / 30) + 1;
      const venusHouse = housePlacements.Venus?.house || 1;
      
      // Use stored exaltation data if available, otherwise calculate
      const venusExaltation = profile.chart_data?.exaltation?.Venus;
      
      const venusState = venusExaltation ? {
        exalted: venusExaltation.exalted,
        debilitated: venusExaltation.debilitated,
        ownSign: venusExaltation.own_sign,
        combust: !!profile.chart_data?.combustion?.Venus?.combust,
        retrograde: housePlacements.Venus?.retrograde || false,
      } : {
        exalted: venusSign === 12, // Pisces
        debilitated: venusSign === 6, // Virgo
        ownSign: venusSign === 2 || venusSign === 7, // Taurus or Libra
        combust: false,
        retrograde: housePlacements.Venus?.retrograde || false,
      };
      
      const jupiterSign = housePlacements.Jupiter?.sign || Math.floor((planetLongitudes.Jupiter || 0) / 30) + 1;
      const jupiterHouse = housePlacements.Jupiter?.house || 1;
      
      const jupiterExaltation = profile.chart_data?.exaltation?.Jupiter;
      const jupiterState = jupiterExaltation ? {
        exalted: jupiterExaltation.exalted,
        debilitated: jupiterExaltation.debilitated,
        ownSign: jupiterExaltation.own_sign,
        combust: !!profile.chart_data?.combustion?.Jupiter?.combust,
        retrograde: housePlacements.Jupiter?.retrograde || false,
      } : {
        exalted: jupiterSign === 4, // Cancer
        debilitated: jupiterSign === 10, // Capricorn
        ownSign: jupiterSign === 9 || jupiterSign === 12, // Sagittarius or Pisces
        combust: false,
        retrograde: housePlacements.Jupiter?.retrograde || false,
      };
      
      const marsSign = housePlacements.Mars?.sign || Math.floor((planetLongitudes.Mars || 0) / 30) + 1;
      const marsHouse = housePlacements.Mars?.house || 1;
      const marsAspects = getPlanetsAspectingHouse(marsHouse, housePlacements);
      
      // Mars state - use stored exaltation data if available
      const marsExaltation = profile.chart_data?.exaltation?.Mars;
      const marsState = marsExaltation ? {
        exalted: marsExaltation.exalted,
        debilitated: marsExaltation.debilitated,
        ownSign: marsExaltation.own_sign,
        combust: !!profile.chart_data?.combustion?.Mars?.combust,
        retrograde: housePlacements.Mars?.retrograde || false,
      } : {
        exalted: marsSign === 10, // Capricorn
        debilitated: marsSign === 4, // Cancer
        ownSign: marsSign === 1 || marsSign === 8, // Aries or Scorpio
        combust: false,
        retrograde: housePlacements.Mars?.retrograde || false,
      };
      
      const saturnSign = housePlacements.Saturn?.sign || Math.floor((planetLongitudes.Saturn || 0) / 30) + 1;
      const rahuSign = housePlacements.Rahu?.sign || Math.floor((planetLongitudes.Rahu || 0) / 30) + 1;

      // Calculate planets aspecting the 7th lord
      const seventhLordAspects = getPlanetsAspectingHouse(seventhLordHouse, housePlacements);

      // ===== D9 (Navamsa) Calculation =====
      // Use stored chart_data if available to ensure consistency with home page
      let d9Data, d9Placements, d9LagnaSign;
      
      if (profile.chart_data?.vargas?.D9) {
        // Use pre-calculated D9 from stored chart_data
        d9Data = profile.chart_data.vargas.D9;
        d9LagnaSign = d9Data.ascSign;
        d9Placements = vargaToPlacements(d9Data);
      } else {
        // Fallback: calculate fresh D9 - need ascendant longitude
        // If we have stored chart_data, use ascendantSign to approximate
        let ascLonDeg: number;
        if (profile.chart_data?.ascendantSign) {
          // Approximate: center of ascendant sign
          ascLonDeg = ((profile.chart_data.ascendantSign - 1) * 30) + 15;
        } else {
          // Last resort: calculate from birth details
          const ascData = getAscendantSignAndNakshatra(isoDate, place_lat, place_lng);
          ascLonDeg = ascData.siderealLongitudeDeg;
        }
        const fullPlanetLongitudes = planetLongitudes as Record<PlanetName, number>;
        const vargas = computeImplementedVargas(ascLonDeg, fullPlanetLongitudes);
        d9Data = vargas.D9;
        d9LagnaSign = d9Data.ascSign;
        d9Placements = vargaToPlacements(d9Data);
      }
      
      // D9 H1 (Lagna) planets - use SIGN-based filtering, not house-based
      const d9PlanetsInH1: PlanetName[] = [];
      Object.entries(d9Data.planetSigns).forEach(([planetName, signNum]) => {
        if (signNum === d9LagnaSign) {
          d9PlanetsInH1.push(planetName as PlanetName);
        }
      });
      
      // D9 H7 planets - use SIGN-based filtering, not house-based
      const d9SeventhSign = ((d9LagnaSign - 1 + 6) % 12) + 1;
      const d9PlanetsInH7: PlanetName[] = [];
      Object.entries(d9Data.planetSigns).forEach(([planetName, signNum]) => {
        if (signNum === d9SeventhSign) {
          d9PlanetsInH7.push(planetName as PlanetName);
        }
      });
      
      // D9 Lords
      const d9LagnaLord = getSignLord(d9LagnaSign);
      const d9SeventhLord = getSignLord(d9SeventhSign);
      const d9LagnaLordSign = d9Data.planetSigns[d9LagnaLord];
      const d9SeventhLordSign = d9Data.planetSigns[d9SeventhLord];
      
      // D9 Lagna Lord State
      const d9LagnaLordState = {
        exalted: (d9LagnaLord === 'Sun' && d9LagnaLordSign === 1) || (d9LagnaLord === 'Moon' && d9LagnaLordSign === 2) || 
                 (d9LagnaLord === 'Mars' && d9LagnaLordSign === 10) || (d9LagnaLord === 'Mercury' && d9LagnaLordSign === 6) ||
                 (d9LagnaLord === 'Jupiter' && d9LagnaLordSign === 4) || (d9LagnaLord === 'Venus' && d9LagnaLordSign === 12) ||
                 (d9LagnaLord === 'Saturn' && d9LagnaLordSign === 7),
        debilitated: (d9LagnaLord === 'Sun' && d9LagnaLordSign === 7) || (d9LagnaLord === 'Moon' && d9LagnaLordSign === 8) ||
                     (d9LagnaLord === 'Mars' && d9LagnaLordSign === 4) || (d9LagnaLord === 'Mercury' && d9LagnaLordSign === 12) ||
                     (d9LagnaLord === 'Jupiter' && d9LagnaLordSign === 10) || (d9LagnaLord === 'Venus' && d9LagnaLordSign === 6) ||
                     (d9LagnaLord === 'Saturn' && d9LagnaLordSign === 1),
        ownSign: getSignLord(d9LagnaLordSign) === d9LagnaLord,
        friendly: true, // Simplified - would need full friendship logic
        combust: false,  // Would need Sun distance calculation
        retrograde: housePlacements[d9LagnaLord]?.retrograde || false, // From stored D1 placements
      };
      
      // D9 7th Lord State
      const d9SeventhLordState = {
        exalted: (d9SeventhLord === 'Jupiter' && d9SeventhLordSign === 4) || (d9SeventhLord === 'Venus' && d9SeventhLordSign === 12),
        debilitated: (d9SeventhLord === 'Jupiter' && d9SeventhLordSign === 10) || (d9SeventhLord === 'Venus' && d9SeventhLordSign === 6),
        ownSign: getSignLord(d9SeventhLordSign) === d9SeventhLord,
        afflicted: false, // Would check aspects
      };
      
      // Venus, Mars, Saturn in D9
      const d9VenusSign = d9Data.planetSigns.Venus;
      const d9VenusHouse = d9Placements.Venus?.house || 1;
      const d9VenusState = {
        exalted: d9VenusSign === 12, // Pisces
        debilitated: d9VenusSign === 6, // Virgo
        ownSign: d9VenusSign === 2 || d9VenusSign === 7,
      };
      
      const d9MarsSign = d9Data.planetSigns.Mars;
      const d9MarsHouse = d9Placements.Mars?.house || 1;
      
      const d9SaturnSign = d9Data.planetSigns.Saturn;
      const d9SaturnHouse = d9Placements.Saturn?.house || 1;

      const partnerChart: PartnerChart = {
        name: profile.full_name,
        gender: (profile.gender?.toLowerCase() || 'male') as 'male' | 'female',
        moonSign,
        moonNakshatra,
        moonNakshatraLord,
        lagna,
        lagnaSign: lagna, // Same as lagna, for clarity in D1 overlays
        h1Score,
        h7Score,
        planetsInH7,
        aspectingH7,
        seventhLord,
        seventhLordSign,
        seventhLordHouse,
        seventhLordState,
        seventhLordAspects,
        // Planet signs for D1 overlays
        marsSign,
        venusSign,
        saturnSign,
        rahuSign,
        mars: {
          house: marsHouse,
          sign: marsSign,
          state: marsState,
          aspectingPlanets: marsAspects,
        },
        venus: {
          sign: venusSign,
          house: venusHouse,
          state: venusState,
        },
        jupiter: {
          sign: jupiterSign,
          house: jupiterHouse,
          state: jupiterState,
        },
        d9: {
          lagnaSign: d9LagnaSign,
          planetsInD9H1: d9PlanetsInH1,
          planetsInD9H7: d9PlanetsInH7,
          d9LagnaLord: d9LagnaLord,
          d9SeventhLord: d9SeventhLord,
          lagnaLordSign: d9LagnaLordSign,
          lagnaLordState: d9LagnaLordState,
          seventhLordSign: d9SeventhLordSign,
          seventhLordState: d9SeventhLordState,
          venusSign: d9VenusSign,
          venusHouse: d9VenusHouse,
          venusState: d9VenusState,
          marsSign: d9MarsSign,
          marsHouse: d9MarsHouse,
          saturnSign: d9SaturnSign,
          saturnHouse: d9SaturnHouse,
        },
      };

      return partnerChart;
    } catch (err) {
      console.error('[MarriageCompatibilityWidget] Error calculating chart:', err);
      return null;
    }
  }

  // Handle profile selection changes
  useEffect(() => {
    if (selectedProfile1) {
      const profile = profiles.find(p => p.id === selectedProfile1);
      if (profile) {
        calculateChartForProfile(profile).then(setPartner1Data);
      }
    }
  }, [selectedProfile1]);

  useEffect(() => {
    if (selectedProfile2) {
      const profile = profiles.find(p => p.id === selectedProfile2);
      if (profile) {
        calculateChartForProfile(profile).then(setPartner2Data);
      }
    }
  }, [selectedProfile2]);

  // Calculate compatibility when both charts are ready
  useEffect(() => {
    if (partner1Data && partner2Data) {
      const calculateCompatibilityAsync = async () => {
        setCalculating(true);
        try {
          // Extract Mangal Dosha from existing profile data (if available)
          const profile1 = profiles.find(p => p.id === selectedProfile1);
          const profile2 = profiles.find(p => p.id === selectedProfile2);
          
          const manglik1 = profile1 ? await extractMangalDoshaFromProfile(profile1, partner1Data.name, partner1Data.mars.house) : undefined;
          const manglik2 = profile2 ? await extractMangalDoshaFromProfile(profile2, partner2Data.name, partner2Data.mars.house) : undefined;
          
          const result = calculateMarriageCompatibility(partner1Data, partner2Data, manglik1, manglik2);
          setCompatibilityResult(result);
        } catch (err) {
          console.error('[MarriageCompatibilityWidget] Error calculating compatibility:', err);
        } finally {
          setCalculating(false);
        }
      };
      
      calculateCompatibilityAsync();
    }
  }, [partner1Data, partner2Data, profiles, selectedProfile1, selectedProfile2, userId]);

  // Get chart data for AdvancedNorthIndianChart
  function getChartDataForPartner(profile: BirthProfile | undefined) {
    if (!profile) return null;
    
    // This is a simplified version - in production, you'd use the full chart data
    // For now, return a placeholder structure
    return {
      planets: [],
      ascendant: {
        rasi: 1,
        degree: 0,
      },
    };
  }

  const profile1 = profiles.find(p => p.id === selectedProfile1);
  const profile2 = profiles.find(p => p.id === selectedProfile2);

  if (loading) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography>Loading birth profiles...</Typography>
      </Box>
    );
  }

  if (profiles.length < 2) {
    return (
      <Alert color="warning" sx={{ m: 2 }}>
        <Stack spacing={1}>
          <Typography level="title-sm">Need at least 2 birth profiles</Typography>
          <Typography level="body-sm">
            {isAdmin 
              ? 'You need at least two saved birth profiles to compare compatibility. Please add more family members in the Birth Details tab.'
              : 'To compare marriage compatibility, add your partner\'s chart through Birth Details. Select "Spouse" or "Other" as the relationship when saving.'}
          </Typography>
          {onNavigateToBirthDetails && (
            <Button size="sm" onClick={onNavigateToBirthDetails}>
              Go to Birth Details
            </Button>
          )}
        </Stack>
      </Alert>
    );
  }

  return (
    <Box sx={{ width: '100%', maxWidth: '100%', p: 0.5, overflow: 'hidden', boxSizing: 'border-box' }}>
      {/* Partner Selection */}
      <Grid container spacing={1} sx={{ mb: 2, width: '100%', maxWidth: '100%' }}>
        <Grid xs={12} md={6}>
          <Typography level="body-sm" sx={{ mb: 0.5, fontSize: '0.8rem', fontWeight: 500 }}>Select Partner 1</Typography>
          <Autocomplete
            size="sm"
            value={profiles.find(p => p.id === selectedProfile1) || null}
            onChange={(_, value) => setSelectedProfile1(value?.id || '')}
            options={profiles}
            getOptionLabel={(option) => `${option.full_name} (${option.gender || 'Unknown'})`}
            placeholder="Type to search or choose first person..."
            slotProps={{
              input: { sx: { fontSize: '0.85rem' } },
              listbox: { sx: { fontSize: '0.85rem' } }
            }}
          />
        </Grid>
        <Grid xs={12} md={6}>
          <Typography level="body-sm" sx={{ mb: 0.5, fontSize: '0.8rem', fontWeight: 500 }}>Select Partner 2</Typography>
          <Autocomplete
            size="sm"
            value={profiles.find(p => p.id === selectedProfile2) || null}
            onChange={(_, value) => setSelectedProfile2(value?.id || '')}
            options={profiles.filter(p => p.id !== selectedProfile1)}
            getOptionLabel={(option) => `${option.full_name} (${option.gender || 'Unknown'})`}
            placeholder="Type to search or choose second person..."
            slotProps={{
              input: { sx: { fontSize: '0.85rem' } },
              listbox: { sx: { fontSize: '0.85rem' } }
            }}
          />
        </Grid>
      </Grid>

      {/* Combined Chart View */}
      {partner1Data && partner2Data && (
        <>
          <Grid container spacing={0.5} sx={{ mb: 2, width: '100%', maxWidth: '100%' }}>
            <Grid xs={12} md={6}>
              <Card variant="outlined">
                <Typography level="title-md" sx={{ mb: 2 }}>{partner1Data.name}</Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Typography level="body-sm">
                    <strong>Lagna (H1):</strong> {RASHI_NAMES[partner1Data.lagna - 1]}
                  </Typography>
                  <Typography level="body-sm">
                    <strong>7th House:</strong> {RASHI_NAMES[((partner1Data.lagna - 1 + 6) % 12)]}
                  </Typography>
                  <Typography level="body-sm">
                    <strong>Moon:</strong> {RASHI_NAMES[partner1Data.moonSign - 1]} in {partner1Data.moonNakshatra}
                  </Typography>
                </Box>
              </Card>
            </Grid>
            <Grid xs={12} md={6}>
              <Card variant="outlined">
                <Typography level="title-md" sx={{ mb: 2 }}>{partner2Data.name}</Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Typography level="body-sm">
                    <strong>Lagna (H1):</strong> {RASHI_NAMES[partner2Data.lagna - 1]}
                  </Typography>
                  <Typography level="body-sm">
                    <strong>7th House:</strong> {RASHI_NAMES[((partner2Data.lagna - 1 + 6) % 12)]}
                  </Typography>
                  <Typography level="body-sm">
                    <strong>Moon:</strong> {RASHI_NAMES[partner2Data.moonSign - 1]} in {partner2Data.moonNakshatra}
                  </Typography>
                </Box>
              </Card>
            </Grid>
          </Grid>

          {/* Overall Compatibility Score */}
          {compatibilityResult && (
            <>
              <Card
                variant="soft"
                color={
                  compatibilityResult.overallRating === 'excellent' ? 'success' :
                  compatibilityResult.overallRating === 'good' ? 'primary' :
                  compatibilityResult.overallRating === 'fair' ? 'warning' : 'danger'
                }
                sx={{ mb: 2, p: 1.5 }}
              >
                <Box sx={{ textAlign: 'center' }}>
                  <Typography level="h2" sx={{ mb: 0.5 }}>
                    {compatibilityResult.overallScore}%
                  </Typography>
                  <Typography level="title-md" sx={{ textTransform: 'capitalize' }}>
                    {compatibilityResult.overallRating} Compatibility
                  </Typography>
                  <Box sx={{ mt: 1.5, bgcolor: 'background.level1', borderRadius: 3, overflow: 'hidden' }}>
                    <LinearProgress
                      determinate
                      size="lg"
                      value={compatibilityResult.overallScore}
                      color={
                        compatibilityResult.overallRating === 'excellent' ? 'success' :
                        compatibilityResult.overallRating === 'good' ? 'primary' :
                        compatibilityResult.overallRating === 'fair' ? 'warning' : 'danger'
                      }
                      sx={{ 
                        '--LinearProgress-thickness': '8px',
                        bgcolor: 'transparent'
                      }}
                    />
                  </Box>
                  
                  {/* Score Breakdown */}
                  <Accordion sx={{ mt: 2 }}>
                    <AccordionSummary>
                      <Typography level="body-sm" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <InfoOutlinedIcon fontSize="small" /> How is this calculated?
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Box sx={{ textAlign: 'left' }}>
                        <Typography level="body-xs" sx={{ mb: 1.5, fontStyle: 'italic' }}>
                          Your compatibility score is calculated from multiple factors. Here's how we weigh each one:
                        </Typography>
                        
                        <Stack spacing={1}>
                          <Box>
                            <Typography level="body-xs" fontWeight="lg" sx={{ mb: 0.25 }}>
                              🔮 D9 Navamsa Chart - Marriage Reality (up to 31 points)
                            </Typography>
                            <Typography level="body-xs" sx={{ pl: 2 }}>
                              The D9 chart reveals how your marriage will actually unfold after the honeymoon phase. We look at:
                              • 7th house planets (marriage house) - Do benefic planets bless it or malefics challenge it?
                              • Venus & Jupiter strength - Your capacity to love and sustain commitment
                              • Identity vs marriage balance (H1-H7) - Will you lose yourself or stay strong?
                              • Cross-chart connections - Does your Venus land in their 7th house? Magic!
                            </Typography>
                          </Box>
                          
                          <Box>
                            <Typography level="body-xs" fontWeight="lg" sx={{ mb: 0.25 }}>
                              💝 Venus & Jupiter Strength (up to 15 points)
                            </Typography>
                            <Typography level="body-xs" sx={{ pl: 2 }}>
                              Venus = ability to love, romance, and adjust. Jupiter = wisdom to sustain marriage through tough times. We check if these planets are exalted, in own sign, debilitated, or combust in your D1 birth charts.
                            </Typography>
                          </Box>
                          
                          <Box>
                            <Typography level="body-xs" fontWeight="lg" sx={{ mb: 0.25 }}>
                              🌙 Ashtakuta - Traditional 8 Tests (up to 15 points)
                            </Typography>
                            <Typography level="body-xs" sx={{ pl: 2 }}>
                              Ancient Moon-based compatibility (Varna, Vashya, Tara, Yoni, Graha Maitri, Gana, Bhakoot, Nadi). Checks temperament match, emotional wavelength, and progeny health. Max 36 points in traditional system.
                            </Typography>
                          </Box>
                          
                          <Box>
                            <Typography level="body-xs" fontWeight="lg" sx={{ mb: 0.25 }}>
                              🌗 Moon Sign Distance (up to 14 points)
                            </Typography>
                            <Typography level="body-xs" sx={{ pl: 2 }}>
                              How far apart are your Moon signs? Same sign = instant emotional connection. 6-8 signs apart = conflicting wavelengths (capped unless D9 is strong).
                            </Typography>
                          </Box>
                          
                          <Box>
                            <Typography level="body-xs" fontWeight="lg" sx={{ mb: 0.25 }}>
                              🏠 7th House Analysis (up to 14 points)
                            </Typography>
                            <Typography level="body-xs" sx={{ pl: 2 }}>
                              Marriage house strength for both partners. Are benefics blessing it? Is the 7th lord well-placed? Jupiter's aspect provides divine protection (+2 points per partner).
                            </Typography>
                          </Box>
                          
                          <Box>
                            <Typography level="body-xs" fontWeight="lg" sx={{ mb: 0.25 }}>
                              🎯 D1 Overlays (±5 points bonus/penalty)
                            </Typography>
                            <Typography level="body-xs" sx={{ pl: 2 }}>
                              When YOUR planets fall in THEIR chart houses: Venus in 7th = romance boost (+2), Moon in 4th = homely comfort (+1.5), Saturn in 7th = karmic stability (+1), Rahu in 7th = obsessive attraction risk (-1).
                            </Typography>
                          </Box>
                          
                          <Box>
                            <Typography level="body-xs" fontWeight="lg" sx={{ mb: 0.25 }}>
                              🔴 Penalties
                            </Typography>
                            <Typography level="body-xs" sx={{ pl: 2 }}>
                              • Mangal Dosha (uncancelled): -5 to -10 points (halved if Mars is strong)
                              • Nadi Dosha: -8 points (reduced to -4 if D9 + Jupiter both strong)
                            </Typography>
                          </Box>
                        </Stack>
                        
                        <Divider sx={{ my: 1.5 }} />
                        <Typography level="body-xs" sx={{ textAlign: 'center', fontWeight: 'lg', bgcolor: 'background.level1', p: 1, borderRadius: 'sm' }}>
                          Total Score Range: 0-100 | Your Score: {compatibilityResult.overallScore}%
                        </Typography>
                        <Typography level="body-xs" sx={{ textAlign: 'center', mt: 0.5, fontStyle: 'italic', color: 'text.tertiary' }}>
                          85+ = Excellent • 70+ = Good • 55+ = Fair • Below 55 = Challenging
                        </Typography>
                      </Box>
                    </AccordionDetails>
                  </Accordion>
                </Box>
              </Card>

              {/* Ashtakuta Traditional Scoring */}
              <Card variant="outlined" sx={{ mb: 2, p: 1, width: '100%', maxWidth: '100%', overflow: 'hidden' }}>
                <Typography level="title-md" sx={{ mb: 1.5 }}>
                  🔢 Ashtakuta (8 Kutas)
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1.5, flexWrap: 'wrap' }}>
                  <Typography level="h3" color={
                    compatibilityResult.ashtakuta.rating === 'excellent' ? 'success' :
                    compatibilityResult.ashtakuta.rating === 'good' ? 'primary' :
                    compatibilityResult.ashtakuta.rating === 'average' ? 'warning' : 'danger'
                  }>
                    {compatibilityResult.ashtakuta.total}/36
                  </Typography>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box sx={{ bgcolor: 'background.level1', borderRadius: 3, overflow: 'hidden' }}>
                      <LinearProgress
                        determinate
                        size="lg"
                        value={(compatibilityResult.ashtakuta.total / 36) * 100}
                        color={
                          compatibilityResult.ashtakuta.rating === 'excellent' ? 'success' :
                          compatibilityResult.ashtakuta.rating === 'good' ? 'primary' :
                          compatibilityResult.ashtakuta.rating === 'average' ? 'warning' : 'danger'
                        }
                        sx={{ 
                          '--LinearProgress-thickness': '8px',
                          bgcolor: 'transparent'
                        }}
                      />
                    </Box>
                    <Typography level="body-xs" sx={{ mt: 0.5, textTransform: 'capitalize' }}>
                      {compatibilityResult.ashtakuta.rating} match
                    </Typography>
                  </Box>
                </Box>
                <Grid container spacing={0.5} sx={{ width: '100%', maxWidth: '100%' }}>
                  <Grid xs={6} sm={3}>
                    <Sheet variant="soft" sx={{ p: 0.5, textAlign: 'center', borderRadius: 'sm', overflow: 'hidden', wordBreak: 'break-word' }}>
                      <Typography level="body-sm" fontWeight="lg">Varna</Typography>
                      <Typography level="h4" sx={{ my: 0.5 }}>{compatibilityResult.ashtakuta.varna.score}/{compatibilityResult.ashtakuta.varna.max}</Typography>
                      <Typography level="body-xs" sx={{ wordBreak: 'break-word', overflowWrap: 'break-word' }}>{compatibilityResult.ashtakuta.varna.description}</Typography>
                    </Sheet>
                  </Grid>
                  <Grid xs={6} sm={3}>
                    <Sheet variant="soft" sx={{ p: 0.5, textAlign: 'center', borderRadius: 'sm', overflow: 'hidden', wordBreak: 'break-word' }}>
                      <Typography level="body-sm" fontWeight="lg">Vashya</Typography>
                      <Typography level="h4" sx={{ my: 0.5 }}>{compatibilityResult.ashtakuta.vashya.score}/{compatibilityResult.ashtakuta.vashya.max}</Typography>
                      <Typography level="body-xs" sx={{ wordBreak: 'break-word', overflowWrap: 'break-word' }}>{compatibilityResult.ashtakuta.vashya.description}</Typography>
                    </Sheet>
                  </Grid>
                  <Grid xs={6} sm={3}>
                    <Sheet variant="soft" sx={{ p: 0.5, textAlign: 'center', borderRadius: 'sm', overflow: 'hidden', wordBreak: 'break-word' }}>
                      <Typography level="body-sm" fontWeight="lg">Tara</Typography>
                      <Typography level="h4" sx={{ my: 0.5 }}>{compatibilityResult.ashtakuta.tara.score}/{compatibilityResult.ashtakuta.tara.max}</Typography>
                      <Typography level="body-xs" sx={{ wordBreak: 'break-word', overflowWrap: 'break-word' }}>{compatibilityResult.ashtakuta.tara.description}</Typography>
                    </Sheet>
                  </Grid>
                  <Grid xs={6} sm={3}>
                    <Sheet variant="soft" sx={{ p: 0.5, textAlign: 'center', borderRadius: 'sm', overflow: 'hidden', wordBreak: 'break-word' }}>
                      <Typography level="body-sm" fontWeight="lg">Yoni</Typography>
                      <Typography level="h4" sx={{ my: 0.5 }}>{compatibilityResult.ashtakuta.yoni.score}/{compatibilityResult.ashtakuta.yoni.max}</Typography>
                      <Typography level="body-xs" sx={{ wordBreak: 'break-word', overflowWrap: 'break-word' }}>{compatibilityResult.ashtakuta.yoni.description}</Typography>
                    </Sheet>
                  </Grid>
                  <Grid xs={6} sm={3}>
                    <Sheet variant="soft" sx={{ p: 0.5, textAlign: 'center', borderRadius: 'sm', overflow: 'hidden', wordBreak: 'break-word' }}>
                      <Typography level="body-sm" fontWeight="lg">Graha Maitri</Typography>
                      <Typography level="h4" sx={{ my: 0.5 }}>{compatibilityResult.ashtakuta.grahaMaitri.score}/{compatibilityResult.ashtakuta.grahaMaitri.max}</Typography>
                      <Typography level="body-xs" sx={{ wordBreak: 'break-word', overflowWrap: 'break-word' }}>{compatibilityResult.ashtakuta.grahaMaitri.description}</Typography>
                    </Sheet>
                  </Grid>
                  <Grid xs={6} sm={3}>
                    <Sheet variant="soft" sx={{ p: 0.5, textAlign: 'center', borderRadius: 'sm', overflow: 'hidden', wordBreak: 'break-word' }}>
                      <Typography level="body-sm" fontWeight="lg">Gana</Typography>
                      <Typography level="h4" sx={{ my: 0.5 }}>{compatibilityResult.ashtakuta.gana.score}/{compatibilityResult.ashtakuta.gana.max}</Typography>
                      <Typography level="body-xs" sx={{ wordBreak: 'break-word', overflowWrap: 'break-word' }}>{compatibilityResult.ashtakuta.gana.description}</Typography>
                    </Sheet>
                  </Grid>
                  <Grid xs={6} sm={3}>
                    <Sheet variant="soft" sx={{ p: 0.5, textAlign: 'center', borderRadius: 'sm', overflow: 'hidden', wordBreak: 'break-word' }}>
                      <Typography level="body-sm" fontWeight="lg">Rashi</Typography>
                      <Typography level="h4" sx={{ my: 0.5 }}>{compatibilityResult.ashtakuta.rashi.score}/{compatibilityResult.ashtakuta.rashi.max}</Typography>
                      <Typography level="body-xs" sx={{ wordBreak: 'break-word', overflowWrap: 'break-word' }}>{compatibilityResult.ashtakuta.rashi.description}</Typography>
                    </Sheet>
                  </Grid>
                  <Grid xs={6} sm={3}>
                    <Sheet variant="soft" sx={{ p: 0.5, textAlign: 'center', borderRadius: 'sm', overflow: 'hidden', wordBreak: 'break-word',
                      bgcolor: compatibilityResult.ashtakuta.nadi.score === 0 ? 'danger.softBg' : undefined }}>
                      <Typography level="body-sm" fontWeight="lg">Nadi</Typography>
                      <Typography level="h4" sx={{ my: 0.5 }} color={compatibilityResult.ashtakuta.nadi.score === 0 ? 'danger' : undefined}>
                        {compatibilityResult.ashtakuta.nadi.score}/{compatibilityResult.ashtakuta.nadi.max}
                      </Typography>
                      <Typography level="body-xs" sx={{ wordBreak: 'break-word', overflowWrap: 'break-word' }}>{compatibilityResult.ashtakuta.nadi.description}</Typography>
                      {compatibilityResult.ashtakuta.nadi.score === 0 && (
                        <Typography level="body-xs" color="danger" sx={{ mt: 0.5 }}>⚠️ Dosha</Typography>
                      )}
                    </Sheet>
                  </Grid>
                </Grid>
              </Card>

              {/* Manglik Dosha Analysis */}
              <Card variant="outlined" sx={{ mb: 2, p: 1.5 }}>
                <Typography level="title-md" sx={{ mb: 1.5 }}>
                  🔴 Manglik Dosha (Kuja Dosha)
                </Typography>
                <Grid container spacing={2}>
                  {/* Person 1 Manglik Analysis */}
                  <Grid xs={12} md={6}>
                    <Card variant="soft" sx={{ p: 1.5 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <Typography level="title-sm">{compatibilityResult.manglikP1.person}</Typography>
                        <Chip 
                          size="sm"
                          color={compatibilityResult.manglikP1.isManglik ? (compatibilityResult.manglikP1.isCancelled ? 'success' : 'danger') : 'neutral'}
                          variant={compatibilityResult.manglikP1.isManglik ? 'solid' : 'soft'}
                        >
                          {compatibilityResult.manglikP1.isManglik ? (
                            compatibilityResult.manglikP1.isCancelled ? 'Manglik - Cancelled ✓' : `Manglik (${compatibilityResult.manglikP1.severity})`
                          ) : 'Not Manglik'}
                        </Chip>
                      </Box>
                      {compatibilityResult.manglikP1.isManglik && (
                        <>
                          <Typography level="body-sm" sx={{ mb: 1 }}>
                            <strong>Mars in House {compatibilityResult.manglikP1.marsHouse}:</strong> {compatibilityResult.manglikP1.effect}
                          </Typography>
                          {compatibilityResult.manglikP1.cancellationFactors.length > 0 && (
                            <Box sx={{ mb: 1 }}>
                              <Typography level="body-sm" fontWeight="lg" color="success">Cancellation Factors:</Typography>
                              {compatibilityResult.manglikP1.cancellationFactors.map((factor, idx) => (
                                <Typography key={idx} level="body-xs" sx={{ ml: 2, mt: 0.5 }}>
                                  ✓ {factor}
                                </Typography>
                              ))}
                            </Box>
                          )}
                        </>
                      )}
                      <Sheet variant="soft" sx={{ p: 1, borderRadius: 'sm', bgcolor: 'background.level1' }}>
                        <Typography level="body-xs">{compatibilityResult.manglikP1.recommendation}</Typography>
                      </Sheet>
                    </Card>
                  </Grid>
                  
                  {/* Person 2 Manglik Analysis */}
                  <Grid xs={12} md={6}>
                    <Card variant="soft" sx={{ p: 1.5 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <Typography level="title-sm">{compatibilityResult.manglikP2.person}</Typography>
                        <Chip 
                          size="sm"
                          color={compatibilityResult.manglikP2.isManglik ? (compatibilityResult.manglikP2.isCancelled ? 'success' : 'danger') : 'neutral'}
                          variant={compatibilityResult.manglikP2.isManglik ? 'solid' : 'soft'}
                        >
                          {compatibilityResult.manglikP2.isManglik ? (
                            compatibilityResult.manglikP2.isCancelled ? 'Manglik - Cancelled ✓' : `Manglik (${compatibilityResult.manglikP2.severity})`
                          ) : 'Not Manglik'}
                        </Chip>
                      </Box>
                      {compatibilityResult.manglikP2.isManglik && (
                        <>
                          <Typography level="body-sm" sx={{ mb: 1 }}>
                            <strong>Mars in House {compatibilityResult.manglikP2.marsHouse}:</strong> {compatibilityResult.manglikP2.effect}
                          </Typography>
                          {compatibilityResult.manglikP2.cancellationFactors.length > 0 && (
                            <Box sx={{ mb: 1 }}>
                              <Typography level="body-sm" fontWeight="lg" color="success">Cancellation Factors:</Typography>
                              {compatibilityResult.manglikP2.cancellationFactors.map((factor, idx) => (
                                <Typography key={idx} level="body-xs" sx={{ ml: 2, mt: 0.5 }}>
                                  ✓ {factor}
                                </Typography>
                              ))}
                            </Box>
                          )}
                        </>
                      )}
                      <Sheet variant="soft" sx={{ p: 1, borderRadius: 'sm', bgcolor: 'background.level1' }}>
                        <Typography level="body-xs">{compatibilityResult.manglikP2.recommendation}</Typography>
                      </Sheet>
                    </Card>
                  </Grid>
                </Grid>
                {compatibilityResult.manglikP1.isManglik && compatibilityResult.manglikP2.isManglik && (
                  <Alert color="success" sx={{ mt: 2 }}>
                    <Typography level="body-sm">
                      <strong>Perfect Match:</strong> Both partners are Manglik - the dosha is automatically neutralized! This is considered an excellent match from Manglik perspective.
                    </Typography>
                  </Alert>
                )}
              </Card>

              {/* Navamsa (D9) - Marriage Reality */}
              {compatibilityResult.d9Analysis && (
                <Card variant="outlined" sx={{ mb: 2, p: 1.5 }}>
                  <Typography level="title-md" sx={{ mb: 1.5 }}>
                    🔮 Navāṁśa (D9) — Marriage Reality
                  </Typography>
                  
                  {/* Overall D9 Rating */}
                  <Alert 
                    color={
                      compatibilityResult.d9Analysis.overallD9Rating === 'excellent' ? 'success' :
                      compatibilityResult.d9Analysis.overallD9Rating === 'good' ? 'primary' :
                      compatibilityResult.d9Analysis.overallD9Rating === 'fair' ? 'warning' : 'danger'
                    }
                    sx={{ mb: 2 }}
                  >
                    <Typography level="body-sm">
                      <strong>D9 Rating: {compatibilityResult.d9Analysis.overallD9Rating.toUpperCase()}</strong><br />
                      {compatibilityResult.d9Analysis.interpretation}
                    </Typography>
                  </Alert>

                  {/* Individual D9 Conditions */}
                  <Grid container spacing={2} sx={{ mb: 2 }}>
                    <Grid xs={12} md={6}>
                      <Card variant="soft" sx={{ p: 1.5 }}>
                        <Typography level="title-sm" sx={{ mb: 1 }}>{compatibilityResult.d9Analysis.person1Name}</Typography>
                        
                        {/* D9 H1 (Lagna) Analysis */}
                        <Typography level="body-xs" fontWeight="bold" sx={{ mb: 0.5 }}>D9 H1 (Identity):</Typography>
                        {compatibilityResult.d9Analysis.p1D9Condition.planetsInD9H1.length > 0 ? (
                          compatibilityResult.d9Analysis.p1D9Condition.planetsInD9H1.map(({ planet, meaning }) => (
                            <Typography key={planet} level="body-xs" sx={{ ml: 1, mb: 0.3 }}>
                              • {planet}: {meaning}
                            </Typography>
                          ))
                        ) : (
                          <Typography level="body-xs" sx={{ ml: 1, mb: 0.3, fontStyle: 'italic', color: 'text.tertiary' }}>
                            No planets in D9 H1
                          </Typography>
                        )}
                        
                        {/* Lagna Lord Strength */}
                        <Typography level="body-xs" sx={{ mt: 0.5, mb: 0.5 }}>
                          D9 Lagna Lord: <Chip size="sm" color={
                            compatibilityResult.d9Analysis.p1D9Condition.lagnaLordStrength === 'strong' ? 'success' :
                            compatibilityResult.d9Analysis.p1D9Condition.lagnaLordStrength === 'weak' ? 'danger' : 'neutral'
                          }>{compatibilityResult.d9Analysis.p1D9Condition.lagnaLordStrength}</Chip>
                        </Typography>
                        <Typography level="body-xs" sx={{ ml: 1, mb: 0.5, fontSize: '0.7rem', color: 'text.tertiary' }}>
                          {compatibilityResult.d9Analysis.p1D9Condition.lagnaLordOutcome}
                        </Typography>
                        
                        {/* H1 Influence */}
                        <Typography level="body-xs" sx={{ mb: 0.5 }}>
                          H1 Influence: <Chip size="sm" color={
                            compatibilityResult.d9Analysis.p1D9Condition.h1Influence === 'benefic-dominant' ? 'success' :
                            compatibilityResult.d9Analysis.p1D9Condition.h1Influence === 'malefic-dominant' ? 'danger' : 'neutral'
                          }>{compatibilityResult.d9Analysis.p1D9Condition.h1Influence}</Chip>
                        </Typography>
                        
                        {/* D9 H7 Analysis */}
                        {compatibilityResult.d9Analysis.p1D9Condition.planetsInD9H7.length > 0 && (
                          <>
                            <Typography level="body-xs" fontWeight="bold" sx={{ mt: 1, mb: 0.5 }}>D9 H7 (Marriage Environment):</Typography>
                            {compatibilityResult.d9Analysis.p1D9Condition.planetsInD9H7.map(({ planet, tone }) => (
                              <Typography key={planet} level="body-xs" sx={{ ml: 1, mb: 0.3 }}>
                                • {planet}: {tone}
                              </Typography>
                            ))}
                          </>
                        )}
                        
                        {compatibilityResult.d9Analysis.p1D9Condition.beneficsInD9H7.length > 0 && (
                          <Typography level="body-xs" sx={{ mb: 0.5 }}>
                            ✅ Benefics in D9 7th: {compatibilityResult.d9Analysis.p1D9Condition.beneficsInD9H7.join(', ')}
                          </Typography>
                        )}
                        {compatibilityResult.d9Analysis.p1D9Condition.maleficsInD9H7.length > 0 && (
                          <Typography level="body-xs" sx={{ mb: 0.5, color: 'danger.500' }}>
                            ⚠️ Malefics in D9 7th: {compatibilityResult.d9Analysis.p1D9Condition.maleficsInD9H7.join(', ')}
                          </Typography>
                        )}
                        
                        <Typography level="body-xs" sx={{ mb: 0.5 }}>
                          D9 Venus: <Chip size="sm" color={
                            compatibilityResult.d9Analysis.p1D9Condition.venusStrength === 'strong' ? 'success' :
                            compatibilityResult.d9Analysis.p1D9Condition.venusStrength === 'weak' ? 'danger' : 'neutral'
                          }>{compatibilityResult.d9Analysis.p1D9Condition.venusStrength}</Chip>
                        </Typography>
                        
                        <Typography level="body-xs" sx={{ mb: 0.5 }}>
                          D9 7th Lord: <Chip size="sm" color={
                            compatibilityResult.d9Analysis.p1D9Condition.seventhLordCondition === 'strong' ? 'success' :
                            compatibilityResult.d9Analysis.p1D9Condition.seventhLordCondition === 'afflicted' ? 'danger' : 'warning'
                          }>{compatibilityResult.d9Analysis.p1D9Condition.seventhLordCondition}</Chip>
                        </Typography>
                        
                        {/* H1-H7 Balance */}
                        <Alert color={
                          compatibilityResult.d9Analysis.p1D9Condition.balancePattern === 'strong-strong' ? 'success' :
                          compatibilityResult.d9Analysis.p1D9Condition.balancePattern === 'weak-weak' ? 'danger' : 'warning'
                        } sx={{ mt: 1, p: 0.5 }}>
                          <Typography level="body-xs" fontWeight="bold">
                            Balance: {compatibilityResult.d9Analysis.p1D9Condition.balancePattern}
                          </Typography>
                          <Typography level="body-xs" sx={{ fontSize: '0.7rem' }}>
                            {compatibilityResult.d9Analysis.p1D9Condition.balanceOutcome}
                          </Typography>
                        </Alert>
                        
                        <Sheet variant="soft" sx={{ p: 1, mt: 1, borderRadius: 'sm', bgcolor: 'background.level1' }}>
                          <Typography level="body-xs">{compatibilityResult.d9Analysis.p1D9Condition.outcome}</Typography>
                        </Sheet>
                      </Card>
                    </Grid>

                    <Grid xs={12} md={6}>
                      <Card variant="soft" sx={{ p: 1.5 }}>
                        <Typography level="title-sm" sx={{ mb: 1 }}>{compatibilityResult.d9Analysis.person2Name}</Typography>
                        
                        {/* D9 H1 (Lagna) Analysis */}
                        <Typography level="body-xs" fontWeight="bold" sx={{ mb: 0.5 }}>D9 H1 (Identity):</Typography>
                        {compatibilityResult.d9Analysis.p2D9Condition.planetsInD9H1.length > 0 ? (
                          compatibilityResult.d9Analysis.p2D9Condition.planetsInD9H1.map(({ planet, meaning }) => (
                            <Typography key={planet} level="body-xs" sx={{ ml: 1, mb: 0.3 }}>
                              • {planet}: {meaning}
                            </Typography>
                          ))
                        ) : (
                          <Typography level="body-xs" sx={{ ml: 1, mb: 0.3, fontStyle: 'italic', color: 'text.tertiary' }}>
                            No planets in D9 H1
                          </Typography>
                        )}
                        
                        {/* Lagna Lord Strength */}
                        <Typography level="body-xs" sx={{ mt: 0.5, mb: 0.5 }}>
                          D9 Lagna Lord: <Chip size="sm" color={
                            compatibilityResult.d9Analysis.p2D9Condition.lagnaLordStrength === 'strong' ? 'success' :
                            compatibilityResult.d9Analysis.p2D9Condition.lagnaLordStrength === 'weak' ? 'danger' : 'neutral'
                          }>{compatibilityResult.d9Analysis.p2D9Condition.lagnaLordStrength}</Chip>
                        </Typography>
                        <Typography level="body-xs" sx={{ ml: 1, mb: 0.5, fontSize: '0.7rem', color: 'text.tertiary' }}>
                          {compatibilityResult.d9Analysis.p2D9Condition.lagnaLordOutcome}
                        </Typography>
                        
                        {/* H1 Influence */}
                        <Typography level="body-xs" sx={{ mb: 0.5 }}>
                          H1 Influence: <Chip size="sm" color={
                            compatibilityResult.d9Analysis.p2D9Condition.h1Influence === 'benefic-dominant' ? 'success' :
                            compatibilityResult.d9Analysis.p2D9Condition.h1Influence === 'malefic-dominant' ? 'danger' : 'neutral'
                          }>{compatibilityResult.d9Analysis.p2D9Condition.h1Influence}</Chip>
                        </Typography>
                        
                        {/* D9 H7 Analysis */}
                        {compatibilityResult.d9Analysis.p2D9Condition.planetsInD9H7.length > 0 && (
                          <>
                            <Typography level="body-xs" fontWeight="bold" sx={{ mt: 1, mb: 0.5 }}>D9 H7 (Marriage Environment):</Typography>
                            {compatibilityResult.d9Analysis.p2D9Condition.planetsInD9H7.map(({ planet, tone }) => (
                              <Typography key={planet} level="body-xs" sx={{ ml: 1, mb: 0.3 }}>
                                • {planet}: {tone}
                              </Typography>
                            ))}
                          </>
                        )}
                        
                        {compatibilityResult.d9Analysis.p2D9Condition.beneficsInD9H7.length > 0 && (
                          <Typography level="body-xs" sx={{ mb: 0.5 }}>
                            ✅ Benefics in D9 7th: {compatibilityResult.d9Analysis.p2D9Condition.beneficsInD9H7.join(', ')}
                          </Typography>
                        )}
                        {compatibilityResult.d9Analysis.p2D9Condition.maleficsInD9H7.length > 0 && (
                          <Typography level="body-xs" sx={{ mb: 0.5, color: 'danger.500' }}>
                            ⚠️ Malefics in D9 7th: {compatibilityResult.d9Analysis.p2D9Condition.maleficsInD9H7.join(', ')}
                          </Typography>
                        )}
                        
                        <Typography level="body-xs" sx={{ mb: 0.5 }}>
                          D9 Venus: <Chip size="sm" color={
                            compatibilityResult.d9Analysis.p2D9Condition.venusStrength === 'strong' ? 'success' :
                            compatibilityResult.d9Analysis.p2D9Condition.venusStrength === 'weak' ? 'danger' : 'neutral'
                          }>{compatibilityResult.d9Analysis.p2D9Condition.venusStrength}</Chip>
                        </Typography>
                        
                        <Typography level="body-xs" sx={{ mb: 0.5 }}>
                          D9 7th Lord: <Chip size="sm" color={
                            compatibilityResult.d9Analysis.p2D9Condition.seventhLordCondition === 'strong' ? 'success' :
                            compatibilityResult.d9Analysis.p2D9Condition.seventhLordCondition === 'afflicted' ? 'danger' : 'warning'
                          }>{compatibilityResult.d9Analysis.p2D9Condition.seventhLordCondition}</Chip>
                        </Typography>
                        
                        {/* H1-H7 Balance */}
                        <Alert color={
                          compatibilityResult.d9Analysis.p2D9Condition.balancePattern === 'strong-strong' ? 'success' :
                          compatibilityResult.d9Analysis.p2D9Condition.balancePattern === 'weak-weak' ? 'danger' : 'warning'
                        } sx={{ mt: 1, p: 0.5 }}>
                          <Typography level="body-xs" fontWeight="bold">
                            Balance: {compatibilityResult.d9Analysis.p2D9Condition.balancePattern}
                          </Typography>
                          <Typography level="body-xs" sx={{ fontSize: '0.7rem' }}>
                            {compatibilityResult.d9Analysis.p2D9Condition.balanceOutcome}
                          </Typography>
                        </Alert>
                        
                        <Sheet variant="soft" sx={{ p: 1, mt: 1, borderRadius: 'sm', bgcolor: 'background.level1' }}>
                          <Typography level="body-xs">{compatibilityResult.d9Analysis.p2D9Condition.outcome}</Typography>
                        </Sheet>
                      </Card>
                    </Grid>
                  </Grid>

                  {/* Cross-Chart Checks */}
                  <Typography level="title-sm" sx={{ mb: 1 }}>Cross-Chart D9 Analysis</Typography>
                  <Stack spacing={0.5}>
                    {compatibilityResult.d9Analysis.crossChartChecks.p1VenusInP2D9H7 && (
                      <Chip size="sm" color="success">✓ {compatibilityResult.d9Analysis.person1Name}'s Venus in {compatibilityResult.d9Analysis.person2Name}'s D9 7th → Strong bonding</Chip>
                    )}
                    {compatibilityResult.d9Analysis.crossChartChecks.p2VenusInP1D9H7 && (
                      <Chip size="sm" color="success">✓ {compatibilityResult.d9Analysis.person2Name}'s Venus in {compatibilityResult.d9Analysis.person1Name}'s D9 7th → Strong bonding</Chip>
                    )}
                    {compatibilityResult.d9Analysis.crossChartChecks.p1VenusAspectsP2D9H7 && (
                      <Chip size="sm" color="primary">✓ {compatibilityResult.d9Analysis.person1Name}'s Venus aspects {compatibilityResult.d9Analysis.person2Name}'s D9 7th → Love flow</Chip>
                    )}
                    {compatibilityResult.d9Analysis.crossChartChecks.p2VenusAspectsP1D9H7 && (
                      <Chip size="sm" color="primary">✓ {compatibilityResult.d9Analysis.person2Name}'s Venus aspects {compatibilityResult.d9Analysis.person1Name}'s D9 7th → Love flow</Chip>
                    )}
                    {compatibilityResult.d9Analysis.crossChartChecks.p1LagnaLordInP2D9Lagna && (
                      <Chip size="sm" color="success">✓ {compatibilityResult.d9Analysis.person1Name}'s Lagna lord in {compatibilityResult.d9Analysis.person2Name}'s D9 Lagna → Mutual support</Chip>
                    )}
                    {compatibilityResult.d9Analysis.crossChartChecks.p2LagnaLordInP1D9Lagna && (
                      <Chip size="sm" color="success">✓ {compatibilityResult.d9Analysis.person2Name}'s Lagna lord in {compatibilityResult.d9Analysis.person1Name}'s D9 Lagna → Mutual support</Chip>
                    )}
                    {compatibilityResult.d9Analysis.crossChartChecks.bothVenusInSameSign && (
                      <Chip size="sm" color="primary">✓ Both Venus in same D9 sign → Similar love language</Chip>
                    )}
                    {compatibilityResult.d9Analysis.crossChartChecks.bothMarsInSameSign && (
                      <Chip size="sm" color="warning">⚡ Both Mars in same D9 sign → Shared passion/aggression</Chip>
                    )}
                    {compatibilityResult.d9Analysis.crossChartChecks.p1MarsAfflictsP2D9H7 && (
                      <Chip size="sm" color="danger">⚠️ {compatibilityResult.d9Analysis.person1Name}'s Mars in {compatibilityResult.d9Analysis.person2Name}'s D9 7th → Conflict cycles</Chip>
                    )}
                    {compatibilityResult.d9Analysis.crossChartChecks.p2MarsAfflictsP1D9H7 && (
                      <Chip size="sm" color="danger">⚠️ {compatibilityResult.d9Analysis.person2Name}'s Mars in {compatibilityResult.d9Analysis.person1Name}'s D9 7th → Conflict cycles</Chip>
                    )}
                    {compatibilityResult.d9Analysis.crossChartChecks.p1MarsAspectsP2D9H7 && (
                      <Chip size="sm" color="warning">⚠️ {compatibilityResult.d9Analysis.person1Name}'s Mars aspects {compatibilityResult.d9Analysis.person2Name}'s D9 7th → Friction</Chip>
                    )}
                    {compatibilityResult.d9Analysis.crossChartChecks.p2MarsAspectsP1D9H7 && (
                      <Chip size="sm" color="warning">⚠️ {compatibilityResult.d9Analysis.person2Name}'s Mars aspects {compatibilityResult.d9Analysis.person1Name}'s D9 7th → Friction</Chip>
                    )}
                    
                    {/* Advanced D9 H1 Overlays */}
                    {compatibilityResult.d9Analysis.crossChartChecks.p1LagnaLordInP2D9H1.present && (
                      <Chip size="sm" color="success" sx={{ bgcolor: 'success.softBg' }}>
                        💫 {compatibilityResult.d9Analysis.crossChartChecks.p1LagnaLordInP2D9H1.meaning}
                      </Chip>
                    )}
                    {compatibilityResult.d9Analysis.crossChartChecks.p2LagnaLordInP1D9H1.present && (
                      <Chip size="sm" color="success" sx={{ bgcolor: 'success.softBg' }}>
                        💫 {compatibilityResult.d9Analysis.crossChartChecks.p2LagnaLordInP1D9H1.meaning}
                      </Chip>
                    )}
                    {compatibilityResult.d9Analysis.crossChartChecks.p1VenusInP2D9H1.present && (
                      <Chip size="sm" color="primary" sx={{ bgcolor: 'primary.softBg' }}>
                        💝 {compatibilityResult.d9Analysis.crossChartChecks.p1VenusInP2D9H1.meaning}
                      </Chip>
                    )}
                    {compatibilityResult.d9Analysis.crossChartChecks.p2VenusInP1D9H1.present && (
                      <Chip size="sm" color="primary" sx={{ bgcolor: 'primary.softBg' }}>
                        💝 {compatibilityResult.d9Analysis.crossChartChecks.p2VenusInP1D9H1.meaning}
                      </Chip>
                    )}
                    {compatibilityResult.d9Analysis.crossChartChecks.p1SaturnInP2D9H1.present && (
                      <Chip size="sm" color="warning" sx={{ bgcolor: 'warning.softBg' }}>
                        ⏳ {compatibilityResult.d9Analysis.crossChartChecks.p1SaturnInP2D9H1.meaning}
                      </Chip>
                    )}
                    {compatibilityResult.d9Analysis.crossChartChecks.p2SaturnInP1D9H1.present && (
                      <Chip size="sm" color="warning" sx={{ bgcolor: 'warning.softBg' }}>
                        ⏳ {compatibilityResult.d9Analysis.crossChartChecks.p2SaturnInP1D9H1.meaning}
                      </Chip>
                    )}
                    {compatibilityResult.d9Analysis.crossChartChecks.p1MarsInP2D9H1.present && (
                      <Chip size="sm" color="danger" sx={{ bgcolor: 'danger.softBg' }}>
                        ⚡ {compatibilityResult.d9Analysis.crossChartChecks.p1MarsInP2D9H1.meaning}
                      </Chip>
                    )}
                    {compatibilityResult.d9Analysis.crossChartChecks.p2MarsInP1D9H1.present && (
                      <Chip size="sm" color="danger" sx={{ bgcolor: 'danger.softBg' }}>
                        ⚡ {compatibilityResult.d9Analysis.crossChartChecks.p2MarsInP1D9H1.meaning}
                      </Chip>
                    )}
                    
                    {!compatibilityResult.d9Analysis.crossChartChecks.p1VenusInP2D9H7 && 
                     !compatibilityResult.d9Analysis.crossChartChecks.p2VenusInP1D9H7 && 
                     !compatibilityResult.d9Analysis.crossChartChecks.p1VenusAspectsP2D9H7 && 
                     !compatibilityResult.d9Analysis.crossChartChecks.p2VenusAspectsP1D9H7 && 
                     !compatibilityResult.d9Analysis.crossChartChecks.p1LagnaLordInP2D9Lagna && 
                     !compatibilityResult.d9Analysis.crossChartChecks.p2LagnaLordInP1D9Lagna && 
                     !compatibilityResult.d9Analysis.crossChartChecks.p1MarsAfflictsP2D9H7 && 
                     !compatibilityResult.d9Analysis.crossChartChecks.p2MarsAfflictsP1D9H7 && 
                     !compatibilityResult.d9Analysis.crossChartChecks.p1MarsAspectsP2D9H7 && 
                     !compatibilityResult.d9Analysis.crossChartChecks.p2MarsAspectsP1D9H7 && 
                     !compatibilityResult.d9Analysis.crossChartChecks.bothVenusInSameSign && 
                     !compatibilityResult.d9Analysis.crossChartChecks.bothMarsInSameSign && (
                      <Typography level="body-xs" sx={{ fontStyle: 'italic' }}>No significant cross-chart interactions detected</Typography>
                    )}
                  </Stack>
                </Card>
              )}

              {/* D1 (Birth Chart) Overlays */}
              {compatibilityResult.d9Analysis?.d1Overlays && (
                <Card variant="outlined" sx={{ mb: 2, p: 1.5 }}>
                  <Typography level="title-md" sx={{ mb: 1.5 }}>
                    🔄 D1 Chart Overlays (Day-to-Day Reality)
                  </Typography>
                  <Typography level="body-xs" sx={{ mb: 1.5, color: 'text.tertiary' }}>
                    How your partner's planets fall in your birth chart (D1) — daily experience
                  </Typography>
                  <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 1 }}>
                    {/* Venus in 7th overlays */}
                    {compatibilityResult.d9Analysis.d1Overlays.p1VenusInP2D1H7.present && (
                      <Chip size="sm" color="success" sx={{ bgcolor: 'success.softBg' }}>
                        💕 {compatibilityResult.d9Analysis.d1Overlays.p1VenusInP2D1H7.meaning}
                      </Chip>
                    )}
                    {compatibilityResult.d9Analysis.d1Overlays.p2VenusInP1D1H7.present && (
                      <Chip size="sm" color="success" sx={{ bgcolor: 'success.softBg' }}>
                        💕 {compatibilityResult.d9Analysis.d1Overlays.p2VenusInP1D1H7.meaning}
                      </Chip>
                    )}
                    
                    {/* Moon in 4th overlays */}
                    {compatibilityResult.d9Analysis.d1Overlays.p1MoonInP2D1H4.present && (
                      <Chip size="sm" color="primary" sx={{ bgcolor: 'primary.softBg' }}>
                        🏡 {compatibilityResult.d9Analysis.d1Overlays.p1MoonInP2D1H4.meaning}
                      </Chip>
                    )}
                    {compatibilityResult.d9Analysis.d1Overlays.p2MoonInP1D1H4.present && (
                      <Chip size="sm" color="primary" sx={{ bgcolor: 'primary.softBg' }}>
                        🏡 {compatibilityResult.d9Analysis.d1Overlays.p2MoonInP1D1H4.meaning}
                      </Chip>
                    )}
                    
                    {/* Mars in 7th overlays */}
                    {compatibilityResult.d9Analysis.d1Overlays.p1MarsInP2D1H7.present && (
                      <Chip size="sm" color="warning" sx={{ bgcolor: 'warning.softBg' }}>
                        ⚔️ {compatibilityResult.d9Analysis.d1Overlays.p1MarsInP2D1H7.meaning}
                      </Chip>
                    )}
                    {compatibilityResult.d9Analysis.d1Overlays.p2MarsInP1D1H7.present && (
                      <Chip size="sm" color="warning" sx={{ bgcolor: 'warning.softBg' }}>
                        ⚔️ {compatibilityResult.d9Analysis.d1Overlays.p2MarsInP1D1H7.meaning}
                      </Chip>
                    )}
                    
                    {/* Saturn in 7th overlays */}
                    {compatibilityResult.d9Analysis.d1Overlays.p1SaturnInP2D1H7.present && (
                      <Chip size="sm" color="neutral" sx={{ bgcolor: 'neutral.softBg' }}>
                        ⏳ {compatibilityResult.d9Analysis.d1Overlays.p1SaturnInP2D1H7.meaning}
                      </Chip>
                    )}
                    {compatibilityResult.d9Analysis.d1Overlays.p2SaturnInP1D1H7.present && (
                      <Chip size="sm" color="neutral" sx={{ bgcolor: 'neutral.softBg' }}>
                        ⏳ {compatibilityResult.d9Analysis.d1Overlays.p2SaturnInP1D1H7.meaning}
                      </Chip>
                    )}
                    
                    {/* Rahu in 7th overlays */}
                    {compatibilityResult.d9Analysis.d1Overlays.p1RahuInP2D1H7.present && (
                      <Chip size="sm" color="danger" sx={{ bgcolor: 'danger.softBg' }}>
                        🌀 {compatibilityResult.d9Analysis.d1Overlays.p1RahuInP2D1H7.meaning}
                      </Chip>
                    )}
                    {compatibilityResult.d9Analysis.d1Overlays.p2RahuInP1D1H7.present && (
                      <Chip size="sm" color="danger" sx={{ bgcolor: 'danger.softBg' }}>
                        🌀 {compatibilityResult.d9Analysis.d1Overlays.p2RahuInP1D1H7.meaning}
                      </Chip>
                    )}
                    
                    {!compatibilityResult.d9Analysis.d1Overlays.p1VenusInP2D1H7.present && 
                     !compatibilityResult.d9Analysis.d1Overlays.p2VenusInP1D1H7.present && 
                     !compatibilityResult.d9Analysis.d1Overlays.p1MoonInP2D1H4.present && 
                     !compatibilityResult.d9Analysis.d1Overlays.p2MoonInP1D1H4.present && 
                     !compatibilityResult.d9Analysis.d1Overlays.p1MarsInP2D1H7.present && 
                     !compatibilityResult.d9Analysis.d1Overlays.p2MarsInP1D1H7.present && 
                     !compatibilityResult.d9Analysis.d1Overlays.p1SaturnInP2D1H7.present && 
                     !compatibilityResult.d9Analysis.d1Overlays.p2SaturnInP1D1H7.present && 
                     !compatibilityResult.d9Analysis.d1Overlays.p1RahuInP2D1H7.present && 
                     !compatibilityResult.d9Analysis.d1Overlays.p2RahuInP1D1H7.present && (
                      <Typography level="body-xs" sx={{ fontStyle: 'italic' }}>No significant D1 overlays detected</Typography>
                    )}
                  </Stack>
                </Card>
              )}

              {/* Emotional Dynamics - Moon Distance */}
              <Card variant="outlined" sx={{ mb: 2, p: 1.5 }}>
                <Typography level="title-md" sx={{ mb: 1.5 }}>
                  💝 Emotional Dynamics
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  <Sheet variant="soft" sx={{ p: 1.5, borderRadius: 'sm' }}>
                    <Typography level="body-sm">
                      <strong>Distance:</strong> {compatibilityResult.moonDistance.distance} ({compatibilityResult.moonDistance.relationshipName})
                    </Typography>
                    <Typography level="body-sm" sx={{ mt: 1 }}>
                      <strong>Meaning:</strong> {compatibilityResult.moonDistance.meaning}
                    </Typography>
                    <Typography level="body-sm" sx={{ mt: 1 }}>
                      {compatibilityResult.moonDistance.outcome}
                    </Typography>
                    <Chip
                      color={
                        compatibilityResult.moonDistance.score === 'high' ? 'success' :
                        compatibilityResult.moonDistance.score === 'moderate' ? 'primary' :
                        compatibilityResult.moonDistance.score === 'conditional' ? 'warning' : 'danger'
                      }
                      sx={{ mt: 2 }}
                    >
                      {compatibilityResult.moonDistance.compatibilityPercent}% Compatible
                    </Chip>
                  </Sheet>
                </Box>
              </Card>

              {/* Gana, Nakshatra Lord, Yoni Compatibility */}
              <Grid container spacing={1} sx={{ mb: 2 }}>
                <Grid xs={12} md={4}>
                  <Card variant="outlined" sx={{ p: 1.5 }}>
                    <Typography level="title-md" sx={{ mb: 1 }}>🌟 Gana (Temperament)</Typography>
                    <Typography level="body-sm" sx={{ mb: 1 }}>
                      {compatibilityResult.gana.combo}
                    </Typography>
                    <Typography level="body-xs" sx={{ mb: 1 }}>
                      {compatibilityResult.gana.outcome}
                    </Typography>
                    <Chip
                      color={
                        compatibilityResult.gana.adjustment === 'easy' ? 'success' :
                        compatibilityResult.gana.adjustment === 'manageable' ? 'primary' :
                        compatibilityResult.gana.adjustment === 'requires-work' ? 'warning' : 'danger'
                      }
                      size="sm"
                    >
                      {compatibilityResult.gana.compatibilityPercent}%
                    </Chip>
                  </Card>
                </Grid>
                <Grid xs={12} md={4}>
                  <Card variant="outlined" sx={{ p: 1.5 }}>
                    <Typography level="title-sm" sx={{ mb: 1 }}>🧠 Nakshatra Lord</Typography>
                    <Typography level="body-sm" sx={{ mb: 1 }}>
                      {compatibilityResult.nakshatraLord.person1Lord} ↔ {compatibilityResult.nakshatraLord.person2Lord}
                    </Typography>
                    <Typography level="body-xs" sx={{ mb: 1 }}>
                      {compatibilityResult.nakshatraLord.outcome}
                    </Typography>
                    <Chip
                      color={
                        compatibilityResult.nakshatraLord.relationship === 'same' || compatibilityResult.nakshatraLord.relationship === 'friendly' ? 'success' :
                        compatibilityResult.nakshatraLord.relationship === 'neutral' ? 'warning' : 'danger'
                      }
                      size="sm"
                    >
                      {compatibilityResult.nakshatraLord.compatibilityPercent}%
                    </Chip>
                  </Card>
                </Grid>
                <Grid xs={12} md={4}>
                  <Card variant="outlined" sx={{ p: 1.5 }}>
                    <Typography level="title-sm" sx={{ mb: 1 }}>🐾 Yoni</Typography>
                    <Typography level="body-sm" sx={{ mb: 1 }}>
                      {compatibilityResult.yoni.person1Yoni} ↔ {compatibilityResult.yoni.person2Yoni}
                    </Typography>
                    <Typography level="body-xs" sx={{ mb: 1 }}>
                      {compatibilityResult.yoni.outcome}
                    </Typography>
                    <Chip
                      color={
                        compatibilityResult.yoni.relationship === 'same' || compatibilityResult.yoni.relationship === 'friendly' ? 'success' :
                        compatibilityResult.yoni.relationship === 'neutral' ? 'warning' : 'danger'
                      }
                      size="sm"
                    >
                      {compatibilityResult.yoni.compatibilityPercent}%
                    </Chip>
                  </Card>
                </Grid>
              </Grid>

              {/* Lagna Compatibility */}
              <Card variant="outlined" sx={{ mb: 2, p: 1.5 }}>
                <Typography level="title-md" sx={{ mb: 1.5 }}>
                  🏠 Lagna (Ascendant) Compatibility
                </Typography>
                <Typography level="body-sm" sx={{ mb: 1 }}>
                  <strong>Distance:</strong> {compatibilityResult.lagna.distance}
                </Typography>
                <Typography level="body-sm" sx={{ mb: 2 }}>
                  {compatibilityResult.lagna.outcome}
                </Typography>
                {compatibilityResult.lagna.flags.length > 0 && (
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    {compatibilityResult.lagna.flags.map((flag, idx) => (
                      <Chip key={idx} size="sm" variant="soft" color="warning" startDecorator={<WarningAmberIcon />}>
                        {flag}
                      </Chip>
                    ))}
                  </Box>
                )}
                <Chip color="primary" sx={{ mt: 2 }}>
                  {compatibilityResult.lagna.compatibilityPercent}% Compatible
                </Chip>
              </Card>

              {/* 7th House Analysis */}
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid xs={12} md={6}>
                  <Card variant="outlined">
                    <Typography level="title-md" sx={{ mb: 2 }}>
                      💍 7th House: {compatibilityResult.seventhHouseP1.person}
                    </Typography>
                    {compatibilityResult.seventhHouseP1.jupiterProtection && (
                      <Alert variant="soft" color="success" sx={{ mb: 2 }}>
                        <Typography level="body-sm" sx={{ fontWeight: 600 }}>
                          ✨ Jupiter's Aspect - Divine Protection for Marriage
                        </Typography>
                      </Alert>
                    )}
                    {compatibilityResult.seventhHouseP1.occupants.length > 0 ? (
                      <Box sx={{ mb: 2 }}>
                        <Typography level="body-sm" sx={{ fontWeight: 600, mb: 1 }}>Occupants:</Typography>
                        {compatibilityResult.seventhHouseP1.occupants.map((occ, idx) => (
                          <Typography key={idx} level="body-xs" sx={{ ml: 2, mb: 0.5 }}>
                            • {occ.planet}: {occ.outcome}
                          </Typography>
                        ))}
                      </Box>
                    ) : (
                      <Typography level="body-sm" sx={{ mb: 2, fontStyle: 'italic' }}>No planets in 7th house</Typography>
                    )}
                    <Divider sx={{ my: 2 }} />
                    <Typography level="body-sm" sx={{ fontWeight: 600, mb: 1 }}>7th Lord ({compatibilityResult.seventhHouseP1.seventhLordCondition.lord}):</Typography>
                    <Typography level="body-xs" sx={{ ml: 2 }}>
                      {compatibilityResult.seventhHouseP1.seventhLordCondition.state}
                    </Typography>
                    <Typography level="body-xs" sx={{ ml: 2, mt: 1 }}>
                      {compatibilityResult.seventhHouseP1.seventhLordCondition.impact}
                    </Typography>
                  </Card>
                </Grid>
                <Grid xs={12} md={6}>
                  <Card variant="outlined">
                    <Typography level="title-md" sx={{ mb: 2 }}>
                      💍 7th House: {compatibilityResult.seventhHouseP2.person}
                    </Typography>
                    {compatibilityResult.seventhHouseP2.jupiterProtection && (
                      <Alert variant="soft" color="success" sx={{ mb: 2 }}>
                        <Typography level="body-sm" sx={{ fontWeight: 600 }}>
                          ✨ Jupiter's Aspect - Divine Protection for Marriage
                        </Typography>
                      </Alert>
                    )}
                    {compatibilityResult.seventhHouseP2.occupants.length > 0 ? (
                      <Box sx={{ mb: 2 }}>
                        <Typography level="body-sm" sx={{ fontWeight: 600, mb: 1 }}>Occupants:</Typography>
                        {compatibilityResult.seventhHouseP2.occupants.map((occ, idx) => (
                          <Typography key={idx} level="body-xs" sx={{ ml: 2, mb: 0.5 }}>
                            • {occ.planet}: {occ.outcome}
                          </Typography>
                        ))}
                      </Box>
                    ) : (
                      <Typography level="body-sm" sx={{ mb: 2, fontStyle: 'italic' }}>No planets in 7th house</Typography>
                    )}
                    <Divider sx={{ my: 2 }} />
                    <Typography level="body-sm" sx={{ fontWeight: 600, mb: 1 }}>7th Lord ({compatibilityResult.seventhHouseP2.seventhLordCondition.lord}):</Typography>
                    <Typography level="body-xs" sx={{ ml: 2 }}>
                      {compatibilityResult.seventhHouseP2.seventhLordCondition.state}
                    </Typography>
                    <Typography level="body-xs" sx={{ ml: 2, mt: 1 }}>
                      {compatibilityResult.seventhHouseP2.seventhLordCondition.impact}
                    </Typography>
                  </Card>
                </Grid>
              </Grid>

              {/* Venus & Jupiter Analysis */}
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid xs={12} md={6}>
                  <Card variant="outlined">
                    <Typography level="title-md" sx={{ mb: 2 }}>
                      💖 Love Capacity: {compatibilityResult.venusJupiterP1.person}
                    </Typography>
                    <Box sx={{ mb: 2 }}>
                      <Typography level="body-sm" sx={{ fontWeight: 600 }}>Venus (Love):</Typography>
                      <Typography level="body-xs" sx={{ ml: 2 }}>{compatibilityResult.venusJupiterP1.venus.state}</Typography>
                      <Typography level="body-xs" sx={{ ml: 2 }}>{compatibilityResult.venusJupiterP1.venus.outcome}</Typography>
                    </Box>
                    <Box sx={{ mb: 2 }}>
                      <Typography level="body-sm" sx={{ fontWeight: 600 }}>Jupiter (Wisdom):</Typography>
                      <Typography level="body-xs" sx={{ ml: 2 }}>{compatibilityResult.venusJupiterP1.jupiter.state}</Typography>
                      <Typography level="body-xs" sx={{ ml: 2 }}>{compatibilityResult.venusJupiterP1.jupiter.outcome}</Typography>
                    </Box>
                    <Sheet variant="soft" sx={{ p: 1.5, borderRadius: 'sm' }}>
                      <Typography level="body-xs">
                        {compatibilityResult.venusJupiterP1.overallCapacity}
                      </Typography>
                    </Sheet>
                  </Card>
                </Grid>
                <Grid xs={12} md={6}>
                  <Card variant="outlined">
                    <Typography level="title-md" sx={{ mb: 2 }}>
                      💖 Love Capacity: {compatibilityResult.venusJupiterP2.person}
                    </Typography>
                    <Box sx={{ mb: 2 }}>
                      <Typography level="body-sm" sx={{ fontWeight: 600 }}>Venus (Love):</Typography>
                      <Typography level="body-xs" sx={{ ml: 2 }}>{compatibilityResult.venusJupiterP2.venus.state}</Typography>
                      <Typography level="body-xs" sx={{ ml: 2 }}>{compatibilityResult.venusJupiterP2.venus.outcome}</Typography>
                    </Box>
                    <Box sx={{ mb: 2 }}>
                      <Typography level="body-sm" sx={{ fontWeight: 600 }}>Jupiter (Wisdom):</Typography>
                      <Typography level="body-xs" sx={{ ml: 2 }}>{compatibilityResult.venusJupiterP2.jupiter.state}</Typography>
                      <Typography level="body-xs" sx={{ ml: 2 }}>{compatibilityResult.venusJupiterP2.jupiter.outcome}</Typography>
                    </Box>
                    <Sheet variant="soft" sx={{ p: 1.5, borderRadius: 'sm' }}>
                      <Typography level="body-xs">
                        {compatibilityResult.venusJupiterP2.overallCapacity}
                      </Typography>
                    </Sheet>
                  </Card>
                </Grid>
              </Grid>

              {/* Strengths, Challenges, Recommendations */}
              <Grid container spacing={1.5}>
                <Grid xs={12} md={4}>
                  <Card variant="outlined" color="success" sx={{ p: 1.5 }}>
                    <Typography level="title-sm" sx={{ mb: 1 }}>✨ Strengths</Typography>
                    {compatibilityResult.strengths.map((strength, idx) => (
                      <Typography key={idx} level="body-sm" sx={{ mb: 1 }}>
                        • {strength}
                      </Typography>
                    ))}
                  </Card>
                </Grid>
                <Grid xs={12} md={4}>
                  <Card variant="outlined" color="warning" sx={{ p: 1.5 }}>
                    <Typography level="title-sm" sx={{ mb: 1 }}>⚠️ Challenges</Typography>
                    {compatibilityResult.challenges.map((challenge, idx) => (
                      <Typography key={idx} level="body-sm" sx={{ mb: 1 }}>
                        • {challenge}
                      </Typography>
                    ))}
                  </Card>
                </Grid>
                <Grid xs={12} md={4}>
                  <Card variant="outlined" color="primary" sx={{ p: 1.5 }}>
                    <Typography level="title-sm" sx={{ mb: 1 }}>💡 Recommendations</Typography>
                    {compatibilityResult.recommendations.map((rec, idx) => (
                      <Typography key={idx} level="body-sm" sx={{ mb: 1 }}>
                        • {rec}
                      </Typography>
                    ))}
                  </Card>
                </Grid>
              </Grid>
            </>
          )}
        </>
      )}

      {calculating && (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography>Calculating compatibility...</Typography>
        </Box>
      )}
    </Box>
  );
}
